'use client'
import Link from 'next/link'
import { useReplicacheQueryV2 } from '~/hooks-data/use-replicache-query'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { teamSpaceMutators } from '@basehub/data/replicache/mutators/team-space/mutators'
import * as React from 'react'
import gsap from 'gsap'
import { getBookmarkDetailsAction } from './get-bookmark-details-action'
import { useQuery } from '@tanstack/react-query'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { SidebarSection } from './components/section'
import { ExpandButton } from './components/expand-button'

export const Bookmarks = () => {
  const { data } = useTeamReplicache()

  if (
    !(data?.team.plan === 'personal' || data?.team.plan_v2?.type === 'personal')
  ) {
    return null
  }

  return <BookmarksList data={data} />
}

const BookmarksList = ({
  data,
}: {
  data: ReturnType<typeof useTeamReplicache>['data']
}) => {
  const bookmarksQuery = useReplicacheQueryV2({
    replicache: data?.replicache,
    fetcher: teamSpaceMutators.listBookmarks,
    key: ['bookmarks', data?.teamId],
  })

  const { data: bookmarks, isInitialLoading: detailsLoading } = useQuery({
    queryFn: async () => {
      if (!bookmarksQuery.data?.length) return null
      return await getBookmarkDetailsAction(bookmarksQuery.data)
    },
    queryKey: [
      'bookmarks-details',
      data?.teamId,
      bookmarksQuery.data?.map((b) => b.id),
    ],
    keepPreviousData: true,
  })

  const [pagination, setPagination] = React.useState({
    page: 1,
    perPage: 10,
  })

  // do we want to fetch and sort all bookmarks here?
  const paginatedData = React.useMemo(() => {
    if (!bookmarks) return []
    return bookmarks
      .sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
      .slice(0, pagination.page * pagination.perPage)
  }, [bookmarks, pagination])

  const handleNextPage = () => {
    if (bookmarks && bookmarks.length <= pagination.page * pagination.perPage) {
      return
    }
    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
  }

  const bookmarksContainerRef = React.useRef<HTMLUListElement>(null)
  const prevPostCount = React.useRef<number | null>(undefined)

  React.useEffect(() => {
    if (bookmarks && !prevPostCount.current) {
      prevPostCount.current = bookmarks.length
    }
    if (
      !bookmarks ||
      !bookmarksContainerRef.current ||
      prevPostCount.current === bookmarks.length
    ) {
      return
    }

    const tl = gsap
      .timeline({ defaults: { duration: 0.63, ease: 'power3.out' } })
      .fromTo(
        bookmarksContainerRef.current.childNodes,
        { yPercent: -100 },
        { yPercent: 0 }
      )

    return () => {
      tl.kill()
      prevPostCount.current = bookmarks.length
    }
  }, [bookmarks])

  return (
    <SidebarSection title="Your bookmarks" className="gap-1.5">
      {(() => {
        if (detailsLoading && bookmarksQuery.data?.length) {
          return (
            <ul ref={bookmarksContainerRef} className="space-y-1.5">
              {Array(bookmarksQuery.data.length)
                .fill('')
                .map((_, i) => (
                  <Skeleton
                    key={i}
                    height={19.5}
                    className="max-w-full"
                    width={120}
                  />
                ))}
            </ul>
          )
        }

        if (!detailsLoading && (!bookmarks || bookmarks.length === 0)) {
          return (
            <p className="text-xs leading-4 text-dark-faint">
              Bookmarks will be listed here
            </p>
          )
        }

        return (
          <div className="overflow-y-auto">
            <ul ref={bookmarksContainerRef} className="space-y-1.5">
              {paginatedData.map((bookmark) => (
                <Link
                  className="text-xs block leading-normal"
                  href={`/${bookmark.team.slug}/${bookmark.repo.slug}`}
                  key={bookmark.id}
                >
                  <span className="text-dark-muted">
                    {bookmark.team.title} /
                  </span>{' '}
                  {bookmark.repo.title}
                </Link>
              ))}
            </ul>
            {!bookmarksQuery.isLoading &&
              bookmarks &&
              bookmarks.length > pagination.page * pagination.perPage && (
                <ExpandButton
                  icon={<ChevronDownIcon />}
                  onClick={handleNextPage}
                  className="mt-2"
                >
                  Load more
                </ExpandButton>
              )}
          </div>
        )
      })()}
    </SidebarSection>
  )
}
